<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import Customers from '@/services/Customers';
import Ticketing from '@/services/Ticketing';
import General from '@/services/General';
import VueBootstrapTypeahead from 'vue-bootstrap-typeahead';
import _ from 'underscore';

export default {
  components: { Layout, PageHeader, VueBootstrapTypeahead },
  page: {
    title: "Ticketing User Data",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  watch: {
    'filterInput.email': _.debounce(function(search) { this.getCustomersAutocomplete(search, 'email') }, 900),
    'filterInput.provider_ticketing_id': _.debounce(function(search) { this.getCustomersAutocomplete(search, 'provider_ticketing_id') }, 900),
  },
  data() {
    return {
      title: "Ticketing User Data",
      items: [
        {
          text: "Ticketing",
          href: "/ticketing",
        },
        {
          text: "Ticketing User Data",
          active: true,
        },
      ],
      filterInput: {
        email: '',
        provider_ticketing_id: '',
      },
      customerData: [],
      tableData: [],
      ticketingData: null,
      isBusy: false,
      ticketsBusy: false,
      error: null,
      seasonTicketsShowAll: [],
      matchTicketsDefault: [],
      matchTicketsByCustomerNumber: [],
      seasonTicketsByCustomerNumber: [],
    };
  },
  computed: {
    customerRow() {
      return Array.isArray(this.tableData) && this.tableData.length
        ? this.tableData[0]
        : null;
    },
    ticketingRow() {
      return this.ticketingData && this.ticketingData.data
        ? this.ticketingData.data
        : null;
    },
    ticketingOriginal() {
      return this.ticketingRow && this.ticketingRow.providerOriginalData
        ? this.ticketingRow.providerOriginalData
        : null;
    },

    // Normalized, comparable values
    crmFullName() {
      if (!this.customerRow) return '';
      const parts = [
        this.customerRow.first_name,
        this.customerRow.middle_name,
        this.customerRow.last_name,
      ].filter(Boolean);
      return parts.join(' ');
    },
    ticketingFullName() {
      if (!this.ticketingOriginal) return '';
      const parts = [
        this.ticketingOriginal.FirstName,
        this.ticketingOriginal.MiddleName,
        this.ticketingOriginal.LastName,
      ].filter(Boolean);
      return parts.join(' ');
    },
    crmAddress() {
      if (!this.customerRow) return '';
      const parts = [
        this.customerRow.address_line1,
        this.customerRow.address_line2,
        this.customerRow.address_line3,
        this.customerRow.address_city,
        this.customerRow.address_postal,
        this.customerRow.address_country,
      ].filter(Boolean);
      return parts.join(' ');
    },
    ticketingAddress() {
      if (!this.ticketingOriginal) return '';
      const parts = [
        this.ticketingOriginal.Street,
        this.ticketingOriginal.StreetNr,
        this.ticketingOriginal.City,
        this.ticketingOriginal.PostalCode,
        this.ticketingRow.countryIso2,
      ].filter(Boolean);
      return parts.join(' ');
    },
    // Difference flags
    fullNameDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        // If we don't have both sides, don't mark as different
        return false;
      }
      return this.areDifferent(this.crmFullName, this.ticketingFullName);
    },
    emailDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.email : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.Email : '';
      return this.areDifferent(crm, tkt);
    },
    dobDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.date_of_birth : '';
      let tkt = this.ticketingOriginal ? this.ticketingOriginal.DateOfBirth : '';
      if (tkt && typeof tkt === 'string' && tkt.includes('T')) {
        tkt = tkt.split('T')[0];
      }
      return this.areDifferent(crm, tkt);
    },
    genderDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.gender : '';
      const tkt = this.ticketingOriginal ? (this.ticketingOriginal.gender || '') : '';
      return this.areDifferent(crm, tkt);
    },
    phoneDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.phone_number : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.TelephoneNr : '';
      return this.areDifferent(crm, tkt);
    },
    addressDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      return this.areDifferent(this.crmAddress, this.ticketingAddress);
    },
    ticketingIdDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.ticketing_provider_id : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.CustomerNumber : '';
      return this.areDifferent(crm, tkt);
    },
    ssoDifferent() {
      if (!this.customerRow || !this.ticketingOriginal) {
        return false;
      }
      const crm = this.customerRow ? this.customerRow.customer_id : '';
      const tkt = this.ticketingOriginal ? this.ticketingOriginal.SSOId : '';
      return this.areDifferent(crm, tkt);
    },
    seasonTicketShowAllRow() {
      return Array.isArray(this.seasonTicketsShowAll) && this.seasonTicketsShowAll.length
        ? this.seasonTicketsShowAll[0]
        : null;
    },
    matchTicketDefaultRow() {
      return Array.isArray(this.matchTicketsDefault) && this.matchTicketsDefault.length
        ? this.matchTicketsDefault[0]
        : null;
    },
    matchTicketByCustomerNumberRow() {
      return Array.isArray(this.matchTicketsByCustomerNumber) && this.matchTicketsByCustomerNumber.length
        ? this.matchTicketsByCustomerNumber[0]
        : null;
    },
    seasonTicketByCustomerNumberRow() {
      return Array.isArray(this.seasonTicketsByCustomerNumber) && this.seasonTicketsByCustomerNumber.length
        ? this.seasonTicketsByCustomerNumber[0]
        : null;
    },
    seasonSectionDifferent() {
      if (!this.seasonTicketShowAllRow || !this.matchTicketDefaultRow) return false;
      return this.areDifferent(this.seasonTicketShowAllRow.area_name, this.matchTicketDefaultRow.ticket_section);
    },
    seasonRowDifferent() {
      if (!this.seasonTicketShowAllRow || !this.matchTicketDefaultRow) return false;
      return this.areDifferent(this.seasonTicketShowAllRow.row_name, this.matchTicketDefaultRow.ticket_row);
    },
    seasonSeatDifferent() {
      if (!this.seasonTicketShowAllRow || !this.matchTicketDefaultRow) return false;
      return this.areDifferent(this.seasonTicketShowAllRow.seat_name, this.matchTicketDefaultRow.ticket_seat);
    },
    seasonClientDifferent() {
      if (!this.seasonTicketShowAllRow || !this.matchTicketDefaultRow) return false;
      return this.areDifferent(this.seasonTicketShowAllRow.client_name, this.matchTicketDefaultRow.client_name);
    },
    seasonEntranceDifferent() {
      if (!this.seasonTicketShowAllRow || !this.matchTicketDefaultRow) return false;
      return this.areDifferent(this.seasonTicketShowAllRow.ticket_entrance, this.matchTicketDefaultRow.ticket_entrance);
    },
    seasonPriceTypeDifferent() {
      if (!this.seasonTicketShowAllRow || !this.matchTicketDefaultRow) return false;
      return this.areDifferent(this.seasonTicketShowAllRow.price_type_name, this.matchTicketDefaultRow.price_type_name);
    },
    customerNumberSectionDifferent() {
      if (!this.matchTicketByCustomerNumberRow || !this.seasonTicketByCustomerNumberRow) return false;
      return this.areDifferent(this.matchTicketByCustomerNumberRow.ticket_section, this.seasonTicketByCustomerNumberRow.area_name);
    },
    customerNumberRowDifferent() {
      if (!this.matchTicketByCustomerNumberRow || !this.seasonTicketByCustomerNumberRow) return false;
      return this.areDifferent(this.matchTicketByCustomerNumberRow.ticket_row, this.seasonTicketByCustomerNumberRow.row_name);
    },
    customerNumberSeatDifferent() {
      if (!this.matchTicketByCustomerNumberRow || !this.seasonTicketByCustomerNumberRow) return false;
      return this.areDifferent(this.matchTicketByCustomerNumberRow.ticket_seat, this.seasonTicketByCustomerNumberRow.seat_name);
    },
    customerNumberClientDifferent() {
      if (!this.matchTicketByCustomerNumberRow || !this.seasonTicketByCustomerNumberRow) return false;
      return this.areDifferent(this.matchTicketByCustomerNumberRow.client_name, this.seasonTicketByCustomerNumberRow.client_name);
    },
    customerNumberEntranceDifferent() {
      if (!this.matchTicketByCustomerNumberRow || !this.seasonTicketByCustomerNumberRow) return false;
      return this.areDifferent(this.matchTicketByCustomerNumberRow.ticket_entrance, this.seasonTicketByCustomerNumberRow.ticket_entrance);
    },
    customerNumberPriceTypeDifferent() {
      if (!this.matchTicketByCustomerNumberRow || !this.seasonTicketByCustomerNumberRow) return false;
      return this.areDifferent(this.matchTicketByCustomerNumberRow.price_type_name, this.seasonTicketByCustomerNumberRow.price_type_name);
    },
  },
  methods: {
    async getCustomersAutocomplete(query, inputTypeName) {
      this.customerData = [];

      if (query.trim().length > 0) {
        let column = '';
        let limit = 1000;
        let table = '';
        let select = [];

        if (inputTypeName === 'email') {
          column = 'email';
          table = 'crm_user';
          select = ['email'];
        } else if (inputTypeName === 'provider_ticketing_id') {
          column = 'CustomerNumber';
          table = 'crm_tymes4';
          select = ['CustomerNumber'];
        }

        const payload = {
          table,
          column,
          filter: query,
          limit,
          select
        };

        try {
          const response = await General.autocomplete(payload);
          const rawData = Array.isArray(response.data.data) ? response.data.data : [];

          const suggestions = rawData.map(item => {
            if (column === 'email') return { text: item.email };
            if (column === 'CustomerNumber') return { text: item.CustomerNumber };
            return null;
          }).filter(Boolean);

          this.customerData = suggestions.filter(
            (v, i, a) => a.findIndex(t => t.text === v.text) === i
          );
        } catch (err) {
          console.error(err);
        }
      }
    },

    async search() {
      this.isBusy = true;
      this.tableData = [];
      this.ticketingData = [];
      this.resetTicketData();
      this.error = null;

      const email = this.filterInput.email.trim();
      const ticketingId = this.filterInput.provider_ticketing_id.trim();

      try {
        // Build filter string for customers endpoint
        let filterString = '';
        if (email) {
          filterString += `email=${encodeURIComponent(email)}`;
        }
        if (ticketingId) {
          filterString += filterString ? '&' : '';
          filterString += `provider_ticketing_id=${encodeURIComponent(ticketingId)}`;
        }

        // Call both endpoints in parallel
        const [customerResponse, ticketingResponse] = await Promise.all([
          filterString ? Customers.getCustomersByFilter(filterString) : Promise.resolve({ data: { data: [] } }),
          (email || ticketingId) ? Ticketing.getUserByCrmId(email || ticketingId) : Promise.resolve({ data: { data: [] } })
        ]);

        this.tableData = customerResponse.data.data || [];

        // Store full ticketing response
        this.ticketingData = ticketingResponse.data || null;

        await this.getCustomerTickets();

      } catch (error) {
        this.error = error.response?.data?.error || 'An error occurred';
        console.error(error);
      } finally {
        this.isBusy = false;
      }
    },

    resetFilters() {
      this.filterInput.email = '';
      this.filterInput.provider_ticketing_id = '';
      this.tableData = [];
      this.ticketingData = null;
      this.resetTicketData();
      this.error = null;
    },

    resetTicketData() {
      this.seasonTicketsShowAll = [];
      this.matchTicketsDefault = [];
      this.matchTicketsByCustomerNumber = [];
      this.seasonTicketsByCustomerNumber = [];
    },

    getSearchCustomerId() {
      if (this.customerRow && this.customerRow.customer_id) {
        return this.customerRow.customer_id;
      }
      if (this.ticketingOriginal && this.ticketingOriginal.SSOId) {
        return this.ticketingOriginal.SSOId;
      }
      return null;
    },

    async getCustomerTickets() {
      const customerId = this.getSearchCustomerId();
      this.resetTicketData();

      if (!customerId) {
        return;
      }

      this.ticketsBusy = true;

      try {
        const [
          seasonShowAllResponse,
          matchDefaultResponse,
          matchByCustomerNumberResponse,
          seasonByCustomerNumberResponse,
        ] = await Promise.all([
          Ticketing.getCustomerSeasonTickets(`show_all=Y&customerID=${encodeURIComponent(customerId)}`),
          Ticketing.getCustomerMatchTickets(`customerID=${encodeURIComponent(customerId)}`),
          Ticketing.getCustomerMatchTickets(`customerID=${encodeURIComponent(customerId)}&ticketsBy=customernumber`),
          Ticketing.getCustomerSeasonTickets(`customerID=${encodeURIComponent(customerId)}&ticketsBy=customernumber`),
        ]);

        this.seasonTicketsShowAll = seasonShowAllResponse.data.data || [];
        this.matchTicketsDefault = matchDefaultResponse.data.data || [];
        this.matchTicketsByCustomerNumber = matchByCustomerNumberResponse.data.data || [];
        this.seasonTicketsByCustomerNumber = seasonByCustomerNumberResponse.data.data || [];
      } catch (error) {
        this.error = error.response?.data?.error || 'An error occurred while loading tickets';
        console.error(error);
      } finally {
        this.ticketsBusy = false;
      }
    },

    formatPrice(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? value : numberValue.toFixed(2);
    },

    areDifferent(a, b) {
      const norm = (val) =>
        (val === null || val === undefined)
          ? ''
          : String(val).trim().toLowerCase();
      return norm(a) !== norm(b);
    },
  },
  middleware: "authentication",
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- Filters Card -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Search Filters</h4>
            <div class="row">
              <div class="col-sm-12 col-md-4">
                <b-form-group label="Email" label-for="formrow-email-input" class="mb-3">
                  <vue-bootstrap-typeahead
                    id="formrow-email-input"
                    ref="emailAutocomplete"
                    v-model.trim="filterInput.email"
                    :data="customerData"
                    :serializer="s => s.text"
                    placeholder="Enter email..."
                  />
                </b-form-group>
              </div>
              <div class="col-sm-12 col-md-4">
                <b-form-group label="Ticketing Customer Number" label-for="formrow-ticketing-id-input" class="mb-3">
                  <vue-bootstrap-typeahead
                    id="formrow-ticketing-id-input"
                    ref="ticketingIdAutocomplete"
                    v-model.trim="filterInput.provider_ticketing_id"
                    :data="customerData"
                    :serializer="s => s.text"
                    placeholder="Enter customer number..."
                  />
                </b-form-group>
              </div>
              <div class="col-sm-12 col-md-4 d-flex align-items-end mb-3">
                <button type="button" class="btn btn-success me-2" @click="search">
                  <i class="mdi mdi-magnify me-1"></i> Search
                </button>
                <button type="button" class="btn btn-primary" @click="resetFilters">
                  <i class="mdi mdi-refresh me-1"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Split Screen Results -->
    <div class="row">
      <!-- Left Side - Customer Data -->
      <div class="col-md-6">
        <div class="card" style="height: 425px;">
          <div class="card-body">
            <h4 class="card-title mb-4">Customer Data</h4>

            <div v-if="isBusy" class="skeleton-wrapper">
              <div v-for="n in 9" :key="n" class="skeleton-row">
                <div class="skeleton-cell skeleton-label"></div>
                <div class="skeleton-cell skeleton-value"></div>
              </div>
            </div>

            <div v-else-if="!customerRow" class="text-muted">
              No customer data found
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th class="w-50">SSO Id</th>
                    <td>{{ customerRow.customer_id }}</td>
                  </tr>
                  <tr>
                    <th>Full Name</th>
                    <td :class="{ 'text-danger': fullNameDifferent }">
                      {{ customerRow.first_name }}
                      <span v-if="customerRow.middle_name"> {{ customerRow.middle_name }}</span>
                      {{ customerRow.last_name }}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td :class="{ 'text-danger': emailDifferent }">
                      {{ customerRow.email }}
                    </td>
                  </tr>
                  <tr>
                    <th>Date of Birth</th>
                    <td :class="{ 'text-danger': dobDifferent }">
                      {{ customerRow.date_of_birth }}
                    </td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td :class="{ 'text-danger': genderDifferent }">
                      {{ customerRow.gender }}
                    </td>
                  </tr>
                  <tr>
                    <th>Address</th>
                    <td :class="{ 'text-danger': addressDifferent }">
                      {{ customerRow.address_line1 }}
                      <span v-if="customerRow.address_line2"> {{ customerRow.address_line2 }}</span>
                      <span v-if="customerRow.address_line3"> {{ customerRow.address_line3 }}</span>,
                      {{ customerRow.address_city }}
                      {{ customerRow.address_postal }},
                      {{ customerRow.address_country }}
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td :class="{ 'text-danger': phoneDifferent }">
                      {{ customerRow.phone_number }}
                    </td>
                  </tr>
                  <tr>
                    <th>Ticketing Customer Number</th>
                    <td :class="{ 'text-danger': ticketingIdDifferent }">
                      {{ customerRow.ticketing_provider_id }}
                    </td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>{{ customerRow.status }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Side - Ticketing Data -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ticketing Data</h4>

            <div v-if="isBusy" class="skeleton-wrapper">
              <div v-for="n in 10" :key="n" class="skeleton-row">
                <div class="skeleton-cell skeleton-label"></div>
                <div class="skeleton-cell skeleton-value"></div>
              </div>
            </div>

            <div v-else-if="!ticketingRow || !ticketingOriginal" class="text-muted">
              No ticketing data found
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm table-bordered mb-0">
                <tbody>
                  <tr>
                    <th>SSO Id</th>
                    <td :class="{ 'text-danger': ssoDifferent }">
                      {{ ticketingOriginal.SSOId }}
                    </td>
                  </tr>
                  <tr>
                    <th>Full Name</th>
                    <td :class="{ 'text-danger': fullNameDifferent }">
                      {{ ticketingOriginal.FirstName }}
                      <span v-if="ticketingOriginal.MiddleName"> {{ ticketingOriginal.MiddleName }}</span>
                      {{ ticketingOriginal.LastName }}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td :class="{ 'text-danger': emailDifferent }">
                      {{ ticketingOriginal.Email }}
                    </td>
                  </tr>
                  <tr>
                    <th>Date Of Birth</th>
                    <td :class="{ 'text-danger': dobDifferent }">
                         {{ ticketingOriginal.DateOfBirth && ticketingOriginal.DateOfBirth.split('T')[0] }}
                    </td>
                  </tr>
                  <tr>
                    <th>Gender</th>
                    <td :class="{ 'text-danger': genderDifferent }">
                      {{ ticketingOriginal.gender }}
                    </td>
                  </tr>
                  <tr>
                    <th>Address</th>
                    <td :class="{ 'text-danger': addressDifferent }">
                      {{ ticketingOriginal.Street }}
                      <span v-if="ticketingOriginal.StreetNr"> {{ ticketingOriginal.StreetNr }}</span>,
                      {{ ticketingOriginal.City }}
                      {{ ticketingOriginal.PostalCode }},
                      {{ ticketingRow.countryIso2 }}
                    </td>
                  </tr>
                  <tr>
                    <th>Phone Number</th>
                    <td :class="{ 'text-danger': phoneDifferent }">
                      {{ ticketingOriginal.TelephoneNr }}
                    </td>
                  </tr>
                  <tr>
                    <th>Ticketing Customer Number</th>
                    <td :class="{ 'text-danger': ticketingIdDifferent }">
                      {{ ticketingOriginal.CustomerNumber }}
                    </td>
                  </tr>
                  <tr>
                    <th class="w-50">Provider</th>
                    <td>{{ ticketingRow.provider }}</td>
                  </tr>
                  <tr>
                    <th>Customer Type</th>
                    <td :class="{ 'text-danger': emailDifferent && ticketingOriginal.CustomerType === 1 }">{{ ticketingOriginal.CustomerType }}</td>
                  </tr>
                  <tr>
                    <th>Sales Group Id</th>
                    <td :class="{ 'text-danger': emailDifferent && ticketingOriginal.CustomerType === 1 }">{{ ticketingOriginal.SalesGroupId }}</td>
                  </tr>

                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ticketing Tickets -->
    <div class="row">
      <!-- Left - Ticketing by SSO ID -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ticketing by SSO ID</h4>

            <div v-if="ticketsBusy" class="skeleton-wrapper">
              <div v-for="n in 14" :key="n" class="skeleton-row">
                <div class="skeleton-cell skeleton-label"></div>
                <div class="skeleton-cell skeleton-value"></div>
              </div>
            </div>

            <template v-else>
              <h5 class="mb-3">Season Tickets</h5>
              <div v-if="!seasonTicketShowAllRow" class="text-muted mb-4">
                No season tickets found
              </div>
              <div v-else class="table-responsive mb-4">
                <table class="table table-sm table-bordered mb-0">
                  <tbody>
                    <tr>
                      <th class="w-50">Season Name</th>
                      <td>{{ seasonTicketShowAllRow.display_name }}</td>
                    </tr>
                    <tr>
                      <th>Subscription Number</th>
                      <td>{{ seasonTicketShowAllRow.subscription_number }}</td>
                    </tr>
                    <tr>
                      <th>Section</th>
                      <td :class="{ 'text-danger': seasonSectionDifferent }">
                        {{ seasonTicketShowAllRow.area_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Row</th>
                      <td :class="{ 'text-danger': seasonRowDifferent }">
                        {{ seasonTicketShowAllRow.row_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Seat</th>
                      <td :class="{ 'text-danger': seasonSeatDifferent }">
                        {{ seasonTicketShowAllRow.seat_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Stand</th>
                      <td>{{ seasonTicketShowAllRow.stand_name }}</td>
                    </tr>
                    <tr>
                      <th>Ticket Desc</th>
                      <td>{{ seasonTicketShowAllRow.ticket_desc }}</td>
                    </tr>
                    <tr>
                      <th>Entrance</th>
                      <td :class="{ 'text-danger': seasonEntranceDifferent }">
                        {{ seasonTicketShowAllRow.ticket_entrance }}
                      </td>
                    </tr>
                    <tr>
                      <th>Price Type</th>
                      <td :class="{ 'text-danger': seasonPriceTypeDifferent }">
                        {{ seasonTicketShowAllRow.price_type_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Client Name</th>
                      <td :class="{ 'text-danger': seasonClientDifferent }">
                        {{ seasonTicketShowAllRow.client_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Price</th>
                      <td>{{ formatPrice(seasonTicketShowAllRow.price) }}</td>
                    </tr>
                    <tr>
                      <th>Transaction Number</th>
                      <td>{{ seasonTicketShowAllRow.transaction_number }}</td>
                    </tr>
                    <tr>
                      <th>Type</th>
                      <td>{{ seasonTicketShowAllRow.type }}</td>
                    </tr>
                    <tr>
                      <th>Siip</th>
                      <td>{{ seasonTicketShowAllRow.siip }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h5 class="mb-3">Match Tickets</h5>
              <div v-if="!matchTicketDefaultRow" class="text-muted">
                No match tickets found
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm table-bordered mb-0">
                  <tbody>
                    <tr>
                      <th class="w-50">Event</th>
                      <td>{{ matchTicketDefaultRow.event_name }}</td>
                    </tr>
                    <tr>
                      <th>Event Date</th>
                      <td>{{ matchTicketDefaultRow.event_date }}</td>
                    </tr>
                    <tr>
                      <th>Ticket ID</th>
                      <td>{{ matchTicketDefaultRow.ticket_id }}</td>
                    </tr>
                    <tr>
                      <th>Section</th>
                      <td :class="{ 'text-danger': seasonSectionDifferent }">
                        {{ matchTicketDefaultRow.ticket_section }}
                      </td>
                    </tr>
                    <tr>
                      <th>Row</th>
                      <td :class="{ 'text-danger': seasonRowDifferent }">
                        {{ matchTicketDefaultRow.ticket_row }}
                      </td>
                    </tr>
                    <tr>
                      <th>Seat</th>
                      <td :class="{ 'text-danger': seasonSeatDifferent }">
                        {{ matchTicketDefaultRow.ticket_seat }}
                      </td>
                    </tr>
                    <tr>
                      <th>Ticket Desc</th>
                      <td>{{ matchTicketDefaultRow.ticket_desc }}</td>
                    </tr>
                    <tr>
                      <th>Entrance</th>
                      <td :class="{ 'text-danger': seasonEntranceDifferent }">
                        {{ matchTicketDefaultRow.ticket_entrance }}
                      </td>
                    </tr>
                    <tr>
                      <th>Price Type</th>
                      <td :class="{ 'text-danger': seasonPriceTypeDifferent }">
                        {{ matchTicketDefaultRow.price_type_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Client Name</th>
                      <td :class="{ 'text-danger': seasonClientDifferent }">
                        {{ matchTicketDefaultRow.client_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Client ID</th>
                      <td>{{ matchTicketDefaultRow.client_id }}</td>
                    </tr>
                    <tr>
                      <th>Ticket Price</th>
                      <td>{{ formatPrice(matchTicketDefaultRow.ticket_price) }}</td>
                    </tr>
                    <tr>
                      <th>Valid</th>
                      <td>{{ matchTicketDefaultRow.is_valid }}</td>
                    </tr>
                    <tr>
                      <th>Cancelled</th>
                      <td>{{ matchTicketDefaultRow.ticket_cancelled }}</td>
                    </tr>
                    <tr>
                      <th>Season Ticket</th>
                      <td>
                        <span v-if="matchTicketDefaultRow.season_ticket">
                          {{ matchTicketDefaultRow.season_ticket.season_name }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Match</th>
                      <td>
                        <span v-if="matchTicketDefaultRow.match">
                          {{ matchTicketDefaultRow.match.home_team_name }} - {{ matchTicketDefaultRow.match.away_team_name }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Siip</th>
                      <td>{{ matchTicketDefaultRow.siip }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Right - Ticketing by Customer Number -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title mb-4">Ticketing by Customer Number</h4>

            <div v-if="ticketsBusy" class="skeleton-wrapper">
              <div v-for="n in 14" :key="n" class="skeleton-row">
                <div class="skeleton-cell skeleton-label"></div>
                <div class="skeleton-cell skeleton-value"></div>
              </div>
            </div>

            <template v-else>
              <h5 class="mb-3">Season Tickets</h5>
              <div v-if="!seasonTicketByCustomerNumberRow" class="text-muted">
                No season tickets found
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm table-bordered mb-4">
                  <tbody>
                    <tr>
                      <th class="w-50">Season Name</th>
                      <td>{{ seasonTicketByCustomerNumberRow.display_name }}</td>
                    </tr>
                    <tr>
                      <th>Subscription Number</th>
                      <td>{{ seasonTicketByCustomerNumberRow.subscription_number }}</td>
                    </tr>
                    <tr>
                      <th>Section</th>
                      <td :class="{ 'text-danger': customerNumberSectionDifferent }">
                        {{ seasonTicketByCustomerNumberRow.area_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Row</th>
                      <td :class="{ 'text-danger': customerNumberRowDifferent }">
                        {{ seasonTicketByCustomerNumberRow.row_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Seat</th>
                      <td :class="{ 'text-danger': customerNumberSeatDifferent }">
                        {{ seasonTicketByCustomerNumberRow.seat_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Stand</th>
                      <td>{{ seasonTicketByCustomerNumberRow.stand_name }}</td>
                    </tr>
                    <tr>
                      <th>Ticket Desc</th>
                      <td>{{ seasonTicketByCustomerNumberRow.ticket_desc }}</td>
                    </tr>
                    <tr>
                      <th>Entrance</th>
                      <td :class="{ 'text-danger': customerNumberEntranceDifferent }">
                        {{ seasonTicketByCustomerNumberRow.ticket_entrance }}
                      </td>
                    </tr>
                    <tr>
                      <th>Price Type</th>
                      <td :class="{ 'text-danger': customerNumberPriceTypeDifferent }">
                        {{ seasonTicketByCustomerNumberRow.price_type_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Client Name</th>
                      <td :class="{ 'text-danger': customerNumberClientDifferent }">
                        {{ seasonTicketByCustomerNumberRow.client_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Price</th>
                      <td>{{ formatPrice(seasonTicketByCustomerNumberRow.price) }}</td>
                    </tr>
                    <tr>
                      <th>Transaction Number</th>
                      <td>{{ seasonTicketByCustomerNumberRow.transaction_number }}</td>
                    </tr>
                    <tr>
                      <th>Type</th>
                      <td>{{ seasonTicketByCustomerNumberRow.type }}</td>
                    </tr>
                    <tr>
                      <th>Siip</th>
                      <td>{{ seasonTicketByCustomerNumberRow.siip }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <h5 >Match Tickets</h5>
              <div v-if="!matchTicketByCustomerNumberRow" class="text-muted mb-4">
                No match tickets found
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm table-bordered mb-0">
                  <tbody>
                    <tr>
                      <th class="w-50">Event</th>
                      <td>{{ matchTicketByCustomerNumberRow.event_name }}</td>
                    </tr>
                    <tr>
                      <th>Event Date</th>
                      <td>{{ matchTicketByCustomerNumberRow.event_date }}</td>
                    </tr>
                    <tr>
                      <th>Ticket ID</th>
                      <td>{{ matchTicketByCustomerNumberRow.ticket_id }}</td>
                    </tr>
                    <tr>
                      <th>Section</th>
                      <td :class="{ 'text-danger': customerNumberSectionDifferent }">
                        {{ matchTicketByCustomerNumberRow.ticket_section }}
                      </td>
                    </tr>
                    <tr>
                      <th>Row</th>
                      <td :class="{ 'text-danger': customerNumberRowDifferent }">
                        {{ matchTicketByCustomerNumberRow.ticket_row }}
                      </td>
                    </tr>
                    <tr>
                      <th>Seat</th>
                      <td :class="{ 'text-danger': customerNumberSeatDifferent }">
                        {{ matchTicketByCustomerNumberRow.ticket_seat }}
                      </td>
                    </tr>
                    <tr>
                      <th>Ticket Desc</th>
                      <td>{{ matchTicketByCustomerNumberRow.ticket_desc }}</td>
                    </tr>
                    <tr>
                      <th>Entrance</th>
                      <td :class="{ 'text-danger': customerNumberEntranceDifferent }">
                        {{ matchTicketByCustomerNumberRow.ticket_entrance }}
                      </td>
                    </tr>
                    <tr>
                      <th>Price Type</th>
                      <td :class="{ 'text-danger': customerNumberPriceTypeDifferent }">
                        {{ matchTicketByCustomerNumberRow.price_type_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Client Name</th>
                      <td :class="{ 'text-danger': customerNumberClientDifferent }">
                        {{ matchTicketByCustomerNumberRow.client_name }}
                      </td>
                    </tr>
                    <tr>
                      <th>Client ID</th>
                      <td>{{ matchTicketByCustomerNumberRow.client_id }}</td>
                    </tr>
                    <tr>
                      <th>Ticket Price</th>
                      <td>{{ formatPrice(matchTicketByCustomerNumberRow.ticket_price) }}</td>
                    </tr>
                    <tr>
                      <th>Valid</th>
                      <td>{{ matchTicketByCustomerNumberRow.is_valid }}</td>
                    </tr>
                    <tr>
                      <th>Cancelled</th>
                      <td>{{ matchTicketByCustomerNumberRow.ticket_cancelled }}</td>
                    </tr>
                    <tr>
                      <th>Season Ticket</th>
                      <td>
                        <span v-if="matchTicketByCustomerNumberRow.season_ticket">
                          {{ matchTicketByCustomerNumberRow.season_ticket.season_name }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Match</th>
                      <td>
                        <span v-if="matchTicketByCustomerNumberRow.match">
                          {{ matchTicketByCustomerNumberRow.match.home_team_name }} - {{ matchTicketByCustomerNumberRow.match.away_team_name }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Siip</th>
                      <td>{{ matchTicketByCustomerNumberRow.siip }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

  </Layout>
</template>

<style scoped>
.skeleton-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.skeleton-row {
  display: flex;
  gap: 12px;
  align-items: center;
}
.skeleton-cell {
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #eef0f2 25%, #f7f8f9 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}
.skeleton-label {
  width: 35%;
}
.skeleton-value {
  width: 55%;
}
@keyframes skeleton-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
</style>